import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { EnumBancoDeDados } from '../../../../../shared/enums';
import { BancoDeDados } from '../../../../../shared/services/api/c4-model/SistemasService';
import { ListaAcessoContainers } from './ListaAcessoContainers';
import { ListaAcessoComponentes } from './ListaAcessoComponentes';
import { TiposBancoDadosService } from '../../../../../shared/services/api/c4-model/TiposBancoDadosService';

type TipoBancoDados = {
  id: number;
  nome: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSave?: (values: BancoDeDados, editId?: string) => void;
  currentSystemId?: string;
  initialValues?: {
    id?: string;
    tipo?: number;
    acessoContainers?: Array<{
      id?: string;
      bancoDeDadosId?: string;
      tipo: any;
      meioAcesso: string;
      acessoComponentes: Array<{
        id?: string;
        nome: string;
      }>;
    }>;
    componentesGerais?: Array<{
      id?: string;
      nome: string;
    }>;
  };
};

export const ModalBancoDeDados: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  currentSystemId,
  initialValues,
}) => {
  const [tipo, setTipo] = useState<number>(EnumBancoDeDados.MSSQL);
  const [tabValue, setTabValue] = useState(0);
  const [tiposBancoDados, setTiposBancoDados] = useState<TipoBancoDados[]>([]);
  const [acessoContainers, setAcessoContainers] = useState<
    Array<{
      id?: string;
      bancoDeDadosId?: string;
      tipo: any;
      meioAcesso: string;
      acessoComponentes: Array<{
        id?: string;
        nome: string;
      }>;
    }>
  >([]);
  // Estado para componentes gerais (não vinculados a containers específicos)
  const [componentesGerais, setComponentesGerais] = useState<
    Array<{
      id?: string;
      nome: string;
    }>
  >([]);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (open) {
      // Carregar tipos de banco de dados
      TiposBancoDadosService.getAll().then((data) => {
        if (Array.isArray(data)) {
          setTiposBancoDados(data);
          // Se não há valores iniciais, definir o primeiro tipo como padrão
          if (!initialValues?.tipo && data.length > 0) {
            setTipo(data[0].id);
          }
        }
      });
    }
  }, [open, initialValues?.tipo]);

  useEffect(() => {
    if (open) {
      if (initialValues) {
        setTipo(initialValues.tipo ?? EnumBancoDeDados.MSSQL);
        setAcessoContainers(initialValues.acessoContainers || []);
        setComponentesGerais(initialValues.componentesGerais || []);
      } else {
        setTipo(EnumBancoDeDados.MSSQL);
        setAcessoContainers([]);
        setComponentesGerais([]);
      }
      setTabValue(0); // Reset tab to first when opening
    }
  }, [open, initialValues]);

  useEffect(() => {
    // Sempre válido já que tipo sempre terá um valor numérico válido
    setIsValid(tipo !== undefined && tipo !== null);
  }, [tipo]);

  const handleSave = () => {
    if (tipo === undefined || tipo === null) {
      return;
    }

    // Mesclar componentes gerais com containers
    let finalAcessoContainers = [...acessoContainers];

    if (componentesGerais.length > 0) {
      if (finalAcessoContainers.length === 0) {
        // Criar um container padrão para os componentes gerais
        finalAcessoContainers.push({
          id: String(Date.now()),
          bancoDeDadosId: initialValues?.id || String(Date.now()),
          tipo: 'WEB', // Tipo padrão
          meioAcesso: 'Direto',
          acessoComponentes: componentesGerais,
        });
      } else {
        // SUBSTITUIR os componentes do primeiro container pelos componentesGerais
        finalAcessoContainers[0] = {
          ...finalAcessoContainers[0],
          acessoComponentes: componentesGerais,
        };
      }
    } else {
      // Se não há componentes gerais, limpar os componentes do primeiro container
      if (finalAcessoContainers.length > 0) {
        finalAcessoContainers[0] = {
          ...finalAcessoContainers[0],
          acessoComponentes: [],
        };
      }
    }

    const values: BancoDeDados = {
      id: initialValues?.id || String(Date.now()),
      sistemaId: currentSystemId || '',
      tipo: tipo,
      acessoContainers: finalAcessoContainers,
    };

    if (onSave) {
      onSave(values, initialValues?.id);
    }
  };

  const handleClose = () => {
    setTipo(EnumBancoDeDados.MSSQL);
    setTiposBancoDados([]);
    setAcessoContainers([]);
    setComponentesGerais([]);
    setTabValue(0);
    onClose();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Função para obter o nome do tipo de banco de dados
  const getTipoBancoDadosNome = (id: number): string => {
    const tipoBd = tiposBancoDados.find((t) => t.id === id);
    return tipoBd?.nome || `Tipo ${id}`;
  };

  return (
    <Dialog open={!!open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        {initialValues?.id ? 'Editar Banco de Dados' : 'Novo Banco de Dados'}
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={3}>
          <FormControl>
            <FormLabel required>Tipo</FormLabel>
            <Select
              fullWidth
              variant="outlined"
              value={tipo}
              onChange={(e) => setTipo(Number(e.target.value))}
            >
              {tiposBancoDados.map((tipoBd) => (
                <MenuItem key={tipoBd.id} value={tipoBd.id}>
                  {tipoBd.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <FormLabel>Acessos</FormLabel>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="acesso tabs"
              >
                <Tab label="Containers" />
                <Tab label="Componentes" />
              </Tabs>
            </Box>
            <Box sx={{ mt: 3 }}>
              {tabValue === 0 && (
                <ListaAcessoContainers
                  initialRows={acessoContainers}
                  onContainersChange={setAcessoContainers}
                  bancoDeDadosId={initialValues?.id}
                />
              )}
              {tabValue === 1 && (
                <ListaAcessoComponentes
                  initialRows={componentesGerais}
                  onComponentesChange={(componentes) => {
                    setComponentesGerais(componentes);
                  }}
                  bancoDeDadosId={initialValues?.id}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          color="primary"
          disabled={!isValid}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
